// src/ui/ReactionBar.tsx (T1): the three reaction buttons with their labels always visible (docs/DECISIONS.md:
// "Love it", "Keep as an option", "Not for us"). Each is a <button aria-pressed> with a text label, never color
// alone (spec 2.9). Tapping the pressed one clears it. Layouts: `row` (icon beside label), `stack` (icon over
// label, narrow cards), `full` (larger, the full "Keep as an option" label, sheets), `round` (the room cards' three
// circles, Not for us / Keep / Love it left to right with the heart at the right edge: the full label is
// screen-reader text and the title, read-only renders nothing; docs/DECISIONS.md, "The board, photos first");
// tone `soft` is the room grid's navy-tint selection. T5's ReactionBar wraps this with the store. Root class:
// hb-reaction-bar.
import type { Reaction } from "../../shared/tenant";
import { cx } from "./cx";
import { Icon, type IconName } from "./Icon";

export const REACTIONS: readonly { value: Reaction; label: string; short: string; icon: IconName }[] = [
  { value: "love", label: "Love it", short: "Love it", icon: "heart" },
  { value: "keep", label: "Keep as an option", short: "Keep", icon: "keep" },
  { value: "pass", label: "Not for us", short: "Not for us", icon: "pass" },
];

export function ReactionBar(props: {
  value: Reaction | null;
  onChange?: (value: Reaction | null) => void;
  layout?: "row" | "stack" | "full" | "round";
  tone?: "solid" | "soft";
  /** Read-only (team mode): the state shows, the buttons do nothing. */
  readOnly?: boolean;
  /** The group's accessible name, e.g. "Your reaction" or "Reacting as Chris". */
  label?: string;
  class?: string;
}) {
  const layout = props.layout ?? "row";
  const round = layout === "round";
  // Round: nobody to react (team mode, no react permission) means no buttons at all; the card's row has the who line.
  if (round && props.readOnly) return null;
  const longLabels = layout === "full" || props.tone === "soft";
  const buttons = round ? [...REACTIONS].reverse() : REACTIONS;
  return (
    <div
      class={cx("hb-reaction-bar", "react", layout === "stack" && "react-stack", layout === "full" && "react-full", round && "react-round", props.tone === "soft" && "react-soft", props.class)}
      role="group"
      aria-label={props.label ?? "Your reaction"}
      data-reaction={props.value ?? "none"}
    >
      {buttons.map((r) => {
        const pressed = props.value === r.value;
        return (
          <button
            key={r.value}
            type="button"
            class="r-btn"
            data-r={r.value}
            aria-pressed={pressed ? "true" : "false"}
            title={longLabels && !round ? undefined : r.label}
            disabled={props.readOnly}
            onClick={() => props.onChange?.(pressed ? null : r.value)}
          >
            <Icon name={r.icon} filled={pressed} />
            {round ? <span class="sr">{r.label}</span> : <span>{longLabels ? r.label : r.short}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** "You passed · Undo" under a passed idea (the card fades; D's .is-passed). */
export function PassedNote(props: { who?: string; onUndo: () => void }) {
  return (
    <p class="hb-passed-note passed-note">
      {props.who ? `${props.who} passed` : "You passed"} ·
      <button class="undo" type="button" onClick={props.onUndo}>
        <Icon name="undo" />
        Undo
      </button>
    </p>
  );
}
